import React from 'react';
import {
    Box,
    Typography,
    Avatar,
    Card,
    Divider,
    Button,
    Tooltip,
    IconButton
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { theme } from '../../../theme';

const ClientCard = ({ client, onResetPassword, onEdit, onDelete }) => {
    const fullName = `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Nom inconnu';
    const initial = fullName.charAt(0).toUpperCase();

    return (
        <Card
            sx={{
                borderRadius: theme.borderRadius.xl,
                border: `1px solid ${theme.colors.border.light}`,
                boxShadow: theme.boxShadow.sm,
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.boxShadow.lg,
                    borderColor: theme.colors.primary[200],
                },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                bgcolor: '#ffffff',
            }}
        >
            {/* Identity Section */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                    sx={{
                        width: 56,
                        height: 56,
                        bgcolor: theme.colors.primary[100],
                        color: theme.colors.primary[700],
                        fontSize: '1.5rem',
                        fontWeight: 800,
                        border: `2px solid #fff`,
                        boxShadow: theme.boxShadow.sm,
                    }}
                >
                    {initial}
                </Avatar>
                <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: theme.colors.text.primary, lineHeight: 1.2 }}>
                        {fullName}
                    </Typography>
                    {/* Removed "Client" tag here */}
                </Box>
            </Box>

            {/* Contact Info - Colorful Icons */}
            <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: theme.colors.warning[50], color: theme.colors.warning[600] }}>
                        <EmailIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" sx={{ color: theme.colors.text.secondary, wordBreak: 'break-all' }}>
                        {client.email}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: theme.colors.success[50], color: theme.colors.success[600] }}>
                        <PhoneIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                        {client.phone || '—'}
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ borderColor: theme.colors.border.light }} />

            {/* Actions Section - Small Icons */}
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <Tooltip title="Modifier le client">
                    <IconButton
                        size="small"
                        onClick={onEdit}
                        sx={{
                            color: theme.colors.text.secondary,
                            '&:hover': { color: theme.colors.primary[600], bgcolor: theme.colors.primary[50] }
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Réinitialiser le mot de passe">
                    <IconButton
                        size="small"
                        onClick={onResetPassword}
                        sx={{
                            color: theme.colors.text.secondary,
                            '&:hover': { color: theme.colors.warning[600], bgcolor: theme.colors.warning[50] }
                        }}
                    >
                        <LockResetIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {onDelete && (
                    <Tooltip title="Supprimer le client">
                        <IconButton
                            size="small"
                            onClick={onDelete}
                            sx={{
                                color: theme.colors.text.secondary,
                                '&:hover': { color: theme.colors.error[600], bgcolor: theme.colors.error[50] }
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Card>
    );
};

export default ClientCard;
